import { dateInputOrder, toLocalDateTime } from "../../ui/date.ts";
import { escapeHtml } from "../../ui/text.ts";
import { parseQuickCapture } from "./capture.ts";

export interface QuickCaptureCapabilities {
  updateRecurrence(form: HTMLFormElement): void;
  updateSummaries(form: HTMLFormElement): void;
  collectionChanged?(form: HTMLFormElement, select: HTMLSelectElement): void;
  now?(): Date;
  dateOrder?(): ReturnType<typeof dateInputOrder>;
}

export function parseComposerTitle(form: HTMLFormElement, capabilities: QuickCaptureCapabilities): void {
  const title = form.elements.namedItem("title") as HTMLInputElement;
  const parsed = parseQuickCapture(title.value, capabilities.now?.() || new Date(), capabilities.dateOrder?.() || dateInputOrder());
  const unresolved: string[] = [];
  title.value = parsed.title;
  if (parsed.dueDate) (form.elements.namedItem("dueDate") as HTMLInputElement).value = parsed.dueDate;
  if (parsed.startAt) (form.elements.namedItem("startAt") as HTMLInputElement).value = toLocalDateTime(parsed.startAt);
  if (parsed.estimatedMinutes) {
    (form.elements.namedItem("estimatedMinutes") as HTMLInputElement).value = String(parsed.estimatedMinutes);
    if (parsed.startAt) (form.elements.namedItem("endAt") as HTMLInputElement).value = toLocalDateTime(new Date(new Date(parsed.startAt).getTime() + parsed.estimatedMinutes * 60_000).toISOString());
  }
  if (parsed.priority) (form.elements.namedItem("priority") as HTMLSelectElement).value = parsed.priority;
  if (parsed.tags.length) {
    const tags = form.elements.namedItem("tags") as HTMLInputElement;
    tags.value = [...new Set([...tags.value.split(",").map(value => value.trim()).filter(Boolean), ...parsed.tags])].join(", ");
  }
  const exactReminder = form.querySelector<HTMLInputElement>("[name=exactReminderAt]");
  if (exactReminder && parsed.reminderAt) exactReminder.value = toLocalDateTime(parsed.reminderAt);
  if (parsed.reminderOffsetMinutes) {
    const preset = form.querySelector<HTMLInputElement>(`[name=reminderOffsets][value="${parsed.reminderOffsetMinutes}"]`);
    if (preset) preset.checked = true;
    else if (exactReminder && (parsed.startAt || parsed.dueDate)) {
      const base = parsed.startAt ? new Date(parsed.startAt) : new Date(`${parsed.dueDate}T09:00:00`);
      exactReminder.value = toLocalDateTime(new Date(base.getTime() - parsed.reminderOffsetMinutes * 60_000).toISOString());
    }
  }
  if (parsed.recurrence) {
    (form.elements.namedItem("recurrenceFrequency") as HTMLSelectElement).value = parsed.recurrence;
    (form.elements.namedItem("recurrenceInterval") as HTMLInputElement).value = String(parsed.recurrenceInterval);
  }
  if (parsed.collection) {
    const select = form.elements.namedItem("categoryId") as HTMLSelectElement;
    const option = Array.from(select.options).find(item => item.textContent?.trim().toLocaleLowerCase() === parsed.collection!.toLocaleLowerCase());
    if (option) { select.value = option.value; capabilities.collectionChanged?.(form, select); }
    else unresolved.push(`list:${parsed.collection.includes(" ") ? `"${parsed.collection}"` : parsed.collection}`);
  }
  const column = form.elements.namedItem("boardColumn") as HTMLSelectElement | null;
  const tagsInput = form.elements.namedItem("tags") as HTMLInputElement;
  if (column && !column.closest<HTMLElement>("[data-composer-column]")?.hidden && parsed.tags.length) {
    const values = new Set(Array.from(column.options).map(option => option.value).filter(Boolean));
    const tags = tagsInput.value.split(",").map(tag => tag.trim().toLowerCase()).filter(Boolean);
    const selected = parsed.tags.find(tag => values.has(tag)) || tags.find(tag => values.has(tag));
    if (selected) column.value = selected;
    tagsInput.value = tags.filter(tag => !values.has(tag)).join(", ");
  }
  if (parsed.assignee) {
    const select = form.elements.namedItem("assignedTo") as HTMLSelectElement;
    const needle = parsed.assignee.toLocaleLowerCase();
    const option = Array.from(select.options).find(item => item.textContent?.toLocaleLowerCase().includes(needle) || item.value.toLocaleLowerCase() === needle);
    if (option) option.selected = true;
    else unresolved.push(`@${parsed.assignee}`);
  }
  if (unresolved.length) title.value = `${title.value} ${unresolved.join(" ")}`.trim();
  capabilities.updateRecurrence(form);
  const status = form.querySelector<HTMLElement>("[data-parse-status]");
  if (status) status.innerHTML = parsed.tokens.length
    ? `<span>Review:</span>${parsed.tokens.map(token => `<span class="parse-token parse-token--${token.kind}">${escapeHtml(token.label)}</span>`).join("")}${unresolved.length ? `<small>Unmatched references were kept in the title.</small>` : ""}`
    : `<small>No supported details found. The title was left unchanged.</small>`;
  if (parsed.startAt || parsed.estimatedMinutes || parsed.reminderAt || parsed.reminderOffsetMinutes || parsed.recurrence) form.querySelector<HTMLDetailsElement>("[data-composer-group=schedule]")!.open = true;
  if (parsed.assignee) form.querySelector<HTMLDetailsElement>("[data-composer-group=collaboration]")!.open = true;
  capabilities.updateSummaries(form);
  title.focus();
}
